import { iframeDataBridge } from '../iframe/iframeDataBridge';
import { mountZone, unmountAll, mountDynamicPointsZone, mountDynamicKpiPosZone, mountDynamicPosMapZone, mountDynamicDatasharZone, mountDynamicCatchmentZone, mountDynamicSalesPlanZone, mountDynamicCatalogCartZone } from '../mount/zoneMount';
import { mountEcosFieldHost, unmountEcosFieldHost } from '../mount/ecosFieldHost';
import { getProductDeliveryLocationDiffusions } from '../api/assort/productDeliveryLocationDiffusion';
import { getResolvedCatchmentAreaMemberCount } from '../api/sales/catchmentArea';
import { log } from '@/utils/core/vueLogger.js';
import { eventBus } from '@/utils/core/eventBus';
import { refreshAppointments } from '../api/sales/appointment';
import { handleLegacyViewChange, navigateToPortal, onLegacyIframeLoad, requestApplicationEntry } from '@/router/legacyNavigationBridge';
import { handleLoginRequired } from '@/utils/auth/sessionService';
import { installLegacyChromeStyle } from './legacyChrome';
import { attachLegacyNavState, detachLegacyNavState } from './legacyNavState';
import { attachLegacyConnectivity, detachLegacyConnectivity } from './legacyConnectivity';
import { attachLegacySession, detachLegacySession } from './legacySession';
import { detachLegacyLoader } from './legacyLoader';
import { exposePinsToLegacy } from '@/utils/legacy/agendaWriter';

let _externisReady = false;

// Poll + amplify-binding state, tracked at module scope so a new iframe load can tear
// down the previous load's poller/subscriptions instead of orphaning them. The old
// checkReady interval closed over the previous contentWindow and, with no attempt cap,
// could poll a stale window forever — pinning the entire old iframe document in memory.
let checkReadyInterval = null;
let boundAmplify = null;
let amplifyHandlers = [];
let agendaUpdatedTimer = null;
const CHECK_READY_MAX_ATTEMPTS = 100; // 100 × 300ms ≈ 30s, then give up for this load

function unbindAmplify() {
	if (agendaUpdatedTimer) {
		clearTimeout(agendaUpdatedTimer);
		agendaUpdatedTimer = null;
	}

	if (boundAmplify && typeof boundAmplify.unsubscribe === 'function') {
		amplifyHandlers.forEach(({ topic, fn }) => {
			try {
				boundAmplify.unsubscribe(topic, fn);
			} catch {
				// stale/dead amplify (document already gone) — nothing to unsubscribe
			}
		});
	}
	amplifyHandlers = [];
	boundAmplify = null;
}

function emitMainSyncCompleted() {
	log('success', 'iframeWatcher', '✅ Main sync completed — emitting main-sync-completed');
	eventBus.emit('main-sync-completed');

	const iframeDoc = iframeDataBridge.getIframeWindow().document;
	if (!iframeDoc.getElementById('vue-points-dialog-zone')) {
		const zoneEl = iframeDoc.createElement('div');
		zoneEl.id = 'vue-points-dialog-zone';
		iframeDoc.body.appendChild(zoneEl);
	}
	mountZone('vue-points-dialog-zone');

	if (!iframeDoc.getElementById('vue-statement-dialogs-zone')) {
		const zoneEl = iframeDoc.createElement('div');
		zoneEl.id = 'vue-statement-dialogs-zone';
		iframeDoc.body.appendChild(zoneEl);
	}
	mountZone('vue-statement-dialogs-zone');

	// Replaces the legacy #mdDl delivery-location picker app-wide (see onModalDeliveryLocationOpened) —
	// mounted once at the iframe root so it's available regardless of the current view.
	if (!iframeDoc.getElementById('vue-delivery-location-modal-zone')) {
		const zoneEl = iframeDoc.createElement('div');
		zoneEl.id = 'vue-delivery-location-modal-zone';
		iframeDoc.body.appendChild(zoneEl);
	}
	mountZone('vue-delivery-location-modal-zone');

	// Route optimization (#23500): the dialog lives at the iframe root, the pin switch in the app-wide appointment modal.
	if (!iframeDoc.getElementById('vue-route-optimizer-zone')) {
		const zoneEl = iframeDoc.createElement('div');
		zoneEl.id = 'vue-route-optimizer-zone';
		iframeDoc.body.appendChild(zoneEl);
	}
	mountZone('vue-route-optimizer-zone');
	mountZone('vue-appointment-pin-zone');
	exposePinsToLegacy();
}

// RG #23004: tell the legacy POS sheet whether a store has datasharing data, so it can hide the tab when empty.
async function probeDatasharing(deliveryLocationId) {
	let hasData = false;
	try {
		const rows = await getProductDeliveryLocationDiffusions({ deliveryLocationId });
		hasData = Array.isArray(rows) && rows.length > 0;
	} catch (error) {
		log('warn', 'iframeWatcher', `Datasharing probe failed for DL ${deliveryLocationId}: ${error.message}`);
	}
	iframeDataBridge.getIframeWindow()?.postMessage({ type: 'DATASHAR_PROBE_RESULT', deliveryLocationId, hasData }, window.location.origin);
}

// Tells the legacy relevé whether the visited store has a catchment area with at least one member,
// so it can hide the "Comparer" entry point when there's nothing to compare against (spec CL-01).
async function probeCatchmentArea(deliveryLocationId) {
	let hasData = false;
	try {
		hasData = (await getResolvedCatchmentAreaMemberCount(deliveryLocationId)) > 0;
	} catch (error) {
		log('warn', 'iframeWatcher', `Catchment area probe failed for DL ${deliveryLocationId}: ${error.message}`);
	}
	iframeDataBridge.getIframeWindow()?.postMessage({ type: 'CATCHMENT_PROBE_RESULT', deliveryLocationId, hasData }, window.location.origin);
}

export function initIframeViewObserver(iframeSelector = 'iframe') {
	const iframe = iframeDataBridge.getIframe();

	// Listen to SYNC_COMPLETED postMessage from iframe — single reliable source
	window.addEventListener('message', (event) => {
		if (event.origin !== window.location.origin) return;
		if (event.data?.type === 'RELOAD_WEBAPP') {
			// Sent by c_reloadWebApp() (back to the portal): the portal is a Vue page, the application waits behind it.
			navigateToPortal();
			return;
		}
		if (event.data?.type === 'LOGIN_REQUIRED') {
			// app/vueSession.js requireLogin(): the legacy refused the session (re-logon or web application refused).
			handleLoginRequired();
			return;
		}
		if (event.data?.type === 'SYNC_COMPLETED' && event.data?.data?.app === 'ALL') {
			emitMainSyncCompleted();
		}
		if (event.data?.type === 'MOUNT_KPI_STATEMENT_ZONE') {
			mountZone('vue-kpi-statement-zone');
		}
		if (event.data?.type === 'MOUNT_ECOS_LIST_FIELDS') {
			mountEcosFieldHost();
		}
		if (event.data?.type === 'MOUNT_POS_MAP_ZONE') {
			const zoneId = event.data?.zoneId;
			if (zoneId) mountDynamicPosMapZone(zoneId);
		}
		if (event.data?.type === 'MOUNT_KPI_POS_ZONE') {
			const zoneId = event.data?.zoneId;
			if (zoneId) mountDynamicKpiPosZone(zoneId);
		}
		if (event.data?.type === 'MOUNT_DATASHAR_ZONE') {
			const zoneId = event.data?.zoneId;
			if (zoneId) mountDynamicDatasharZone(zoneId);
		}
		if (event.data?.type === 'MOUNT_FUTURE_REFERENCING_ZONE') {
			mountZone('vue-future-referencing-zone');
		}
		if (event.data?.type === 'MOUNT_SALES_PLAN_ZONE') {
			const zoneId = event.data?.zoneId;
			if (zoneId) mountDynamicSalesPlanZone(zoneId);
		}
		if (event.data?.type === 'MOUNT_CATALOG_CART_ZONE') {
			const zoneId = event.data?.zoneId;
			if (zoneId) mountDynamicCatalogCartZone(zoneId);
		}
		if (event.data?.type === 'DATASHAR_PROBE') {
			const deliveryLocationId = event.data?.deliveryLocationId;
			if (deliveryLocationId != null) probeDatasharing(deliveryLocationId);
		}
		if (event.data?.type === 'MOUNT_CATCHMENT_ZONE') {
			const zoneId = event.data?.zoneId;
			if (zoneId) mountDynamicCatchmentZone(zoneId);
		}
		if (event.data?.type === 'CATCHMENT_PROBE') {
			const deliveryLocationId = event.data?.deliveryLocationId;
			if (deliveryLocationId != null) probeCatchmentArea(deliveryLocationId);
		}
		if (event.data?.type === 'OPEN_CATCHMENT_COMPARISON') {
			const { deliveryLocationId, localReferenceStatement } = event.data || {};
			if (deliveryLocationId != null) {
				window.dispatchEvent(new CustomEvent('open-vue-catchment-comparison', { detail: { deliveryLocationId, localReferenceStatement: localReferenceStatement ?? null } }));
			}
		}
		if (event.data?.type === 'OPEN_COMPETITOR_POSITIONING') {
			const { deliveryLocationId, deliveryLocationLabel, statementDate, statements } = event.data || {};
			window.dispatchEvent(new CustomEvent('open-vue-competitor-positioning', { detail: { deliveryLocationId, deliveryLocationLabel: deliveryLocationLabel || '', statementDate: statementDate ?? null, statements: Array.isArray(statements) ? statements : [] } }));
		}
		if (event.data?.type === 'OPEN_ROUTE_OPTIMIZER') {
			const { viewType, start, end } = event.data || {};
			if (start && end) window.dispatchEvent(new CustomEvent('open-vue-route-optimizer', { detail: { viewType, start, end } }));
		}
		if (event.data?.type === 'MOUNT_POINTS_STATEMENT_ZONE') {
			const zoneId = event.data?.zoneId;
			if (zoneId) {
				mountDynamicPointsZone(zoneId);
			} else {
				mountZone('vue-points-statement-zone');
			}
		}
	});

	iframe.onload = () => {
		// Each iframe navigation = fresh document → reset so externis-ready can fire again.
		// Without this, reconcile only runs at the login page (user not yet connected) and
		// the applicationResources fetch is never triggered after login.
		_externisReady = false;

		// Tear down the previous load's poller + amplify bindings so they can't keep
		// polling/pinning the old (now-detached) iframe document.
		if (checkReadyInterval) {
			clearInterval(checkReadyInterval);
			checkReadyInterval = null;
		}
		unbindAmplify();
		unmountEcosFieldHost();
		detachLegacyNavState();
		detachLegacyConnectivity();
		detachLegacySession();
		detachLegacyLoader();

		const win = iframeDataBridge.getIframeWindow();
		onLegacyIframeLoad(win);
		installLegacyChromeStyle(win);
		let attempts = 0;

		// Periodically check if amplify and externis are ready in the iframe.
		// Both must be present before we subscribe or stop polling — if clearInterval fires
		// while amplify.subscribe is available but externis isn't, externis-ready would never
		// be emitted for this page load.
		checkReadyInterval = setInterval(() => {
			attempts++;
			try {
				if (win?.amplify && win?.externis) {
					window.amplify = win.amplify;
					log('info', 'iframeWatcher', 'Added amplify to window');

					if (!_externisReady) {
						_externisReady = true;
						eventBus.emit('externis-ready');
						attachLegacyNavState(win);
						attachLegacyConnectivity(win);
						if (attachLegacySession(win)) requestApplicationEntry();
					}

					if (win.amplify.subscribe) {
						const onEvtChangeView = (evt) => {
							// First, so view-changed consumers and zone mounts below observe the settled URL.
							// Guarded: a router failure must never break the zone pipeline.
							try {
								handleLegacyViewChange(evt);
							} catch (error) {
								log('error', 'iframeWatcher', `Router bridge failed: ${error.message}`);
							}
							unmountAll();
							unmountEcosFieldHost();
							const useOwnOnly = win?.externis?.filterByOwnDeliveryLocations ?? !iframeDataBridge.isOnline();
							const sourceList = useOwnOnly ? win?.externis?.ecosVM?.ownDeliveryLocations : win.externis.ecosVM.deliveryLocations || [];

							const view = evt?.view || evt?.currentView;
							console.log('View changed to:', view);
							eventBus.emit('view-changed', { view });
							log('info', 'iframeWatcher', `View changed to: ${view}`);

							switch (view || win.externis?.ecosVM?.currentView?.()) {
								case 'widgets':
								case 'vwWidgets':
									const vueWidgetOption = win.externis?.ecosVM.options.find((o) => o.name == 'VueWidgets');

									if (vueWidgetOption && vueWidgetOption.value === 'true') {
										mountZone('vue-widgets-zone');
									}
									break;

								case 'vwSFAPlanning':
									exposePinsToLegacy();
									mountZone('vue-calendar-map-zone', { needFilter: true });
									mountZone('vue-delivery-location-form-zone');
									mountZone('vue-delivery-location-filter-dialog-zone', {
										needAnchor: true,
										locations: sourceList,
										needSfaFilter: false,
										needLegacyBroadcast: true,
									});
									break;

								case 'vwSFAPOS':
									mountZone('vue-delivery-location-form-zone');
									mountZone('vue-delivery-location-filter-dialog-zone', {
										needAnchor: true,
										locations: sourceList,
										needSfaFilter: true,
										needLegacyBroadcast: true,
									});
									break;

								case 'vw-visit':
									mountZone('vue-kpi-statement-zone');
									break;
							}
						};

						// The login → home transition sets ecosVM.currentView('greeter') DIRECTLY
						// (eagle.js), it does NOT publish evtChangeView, and it stays in the same
						// document (no iframe.onload) — so neither 'view-changed' nor 'externis-ready'
						// fires after login. evtUserChanged IS published the moment the user context
						// is set (login.js / SSO Startup.js), so it's the reliable "now connected"
						// signal that lets i18n bootstrap fetch the applicationResources overlay.
						const onEvtUserChanged = (evt) => {
							eventBus.emit('user-changed', evt);
							log('info', 'iframeWatcher', 'User changed — emitting user-changed');
						};

						const onAppointmentUpdated = async (data) => {
							const deliveryLocationId = data?.deliveryLocationId ?? null;
							log('info', 'Points', `🎯[CLAIM-DEBUG] legacy AppointmentUpdated received — online=${iframeDataBridge.isOnline()} dlId=${deliveryLocationId} uid=${data?.appointmentUid ?? 'none'}`);
							if (iframeDataBridge.isOnline()) {
								await refreshAppointments(deliveryLocationId);
							}
							// Forward the fresh post-synch appointmentUid too — dropping it here forces
							// every deferred points claim (see PointsClaimButton/PointsDialogZone) to fall
							// back to a possibly-stale/temp uid and take the offline-queue path even when
							// online and the visit just got a real server uid (RG #22908).
							eventBus.emit('appointments-updated', { deliveryLocationId, appointmentUid: data?.appointmentUid ?? null });
						};

						const onDeliveryLocationCreated = () => {
							eventBus.emit('delivery-location-created');
						};

						// The legacy agenda announces every appointment create/move/delete/cancel with amplify
						// 'evtAgendaChange' (view-level refresh) / 'evtAgendaChanged' (post-save payload). Neither
						// ever reached Vue, so planning a visit from the agenda left WidgetMyDay/WidgetMyArea on
						// stale data until their selectedDate watch re-fired — the "go to the previous/next day and
						// come back" workaround QA hit on CS1. Deliberately NOT relayed as 'appointments-updated':
						// that topic means "a visit was validated" and drives the points claim submission (#22908),
						// which must not fire on a mere planning change. Debounced because a single agenda action
						// publishes in bursts (save → 750ms → forced refresh).
						const onAgendaChanged = () => {
							if (agendaUpdatedTimer) clearTimeout(agendaUpdatedTimer);
							agendaUpdatedTimer = setTimeout(() => {
								agendaUpdatedTimer = null;
								log('info', 'iframeWatcher', 'Agenda changed — emitting agenda-updated');
								eventBus.emit('agenda-updated');
							}, 400);
						};

						// A visit was deleted or cancelled before ever being validated — any points claim
						// the user made mid-visit was only ever stored locally (deferred submission, RG
						// #22908) and must not survive to attach itself to whichever visit gets validated
						// next for the same store. 'evtAppointmentDeleted' (added alongside the
						// pre-existing, more broadly-scoped 'evtAgendaChanged'/'evtExternalAppointmentDeleted')
						// fires on every delete AND cancel exit path (sfaHelper's deleteAppointment AND
						// cancelAppointment — a cancelled-but-past-dated visit is just re-saved with status
						// CANCELLED, never routed through deleteAppointment) and only on delete/cancel,
						// unlike 'evtAgendaChanged' which also fires on plain appointment saves.
						const onAppointmentDeleted = (data) => {
							const deliveryLocationId = data?.deliveryLocationId ?? null;
							if (deliveryLocationId == null) return;
							log('info', 'Points', `🎯[CLAIM-DEBUG] appointment deleted/cancelled — purging pending claims for dlId=${deliveryLocationId}`);
							eventBus.emit('appointment-deleted', { deliveryLocationId });
						};

						const onModalDeliveryLocationOpened = (params) => {
							// Every caller (vwSalesCatalog, vwSFAPlanning, vwOrdersTracking, vwSFAPOS,
							// appointment.js, ...) now opens the native Vue DeliveryLocationDialog (persistent
							// zone mounted app-wide, see emitMainSyncCompleted) instead of the legacy KO #mdDl
							// modal — forward the same { sender, authorizedDLT, dlId } params evtModalDL was
							// published with.
							window.dispatchEvent(new CustomEvent('open-vue-delivery-location-modal', { detail: params ?? {} }));
						};

						win.amplify.subscribe('evtModalDL', onModalDeliveryLocationOpened);
						win.amplify.subscribe('evtChangeView', onEvtChangeView);
						win.amplify.subscribe('evtUserChanged', onEvtUserChanged);
						win.amplify.subscribe('AppointmentUpdated', onAppointmentUpdated);
						win.amplify.subscribe('DeliveryLocationCreated', onDeliveryLocationCreated);
						win.amplify.subscribe('evtAgendaChange', onAgendaChanged);
						win.amplify.subscribe('evtAgendaChanged', onAgendaChanged);
						win.amplify.subscribe('evtAppointmentDeleted', onAppointmentDeleted);

						// Remember what we bound so the next load can unsubscribe it.
						boundAmplify = win.amplify;
						amplifyHandlers = [
							{ topic: 'evtModalDL', fn: onModalDeliveryLocationOpened },
							{ topic: 'evtChangeView', fn: onEvtChangeView },
							{ topic: 'evtUserChanged', fn: onEvtUserChanged },
							{ topic: 'AppointmentUpdated', fn: onAppointmentUpdated },
							{ topic: 'DeliveryLocationCreated', fn: onDeliveryLocationCreated },
							{ topic: 'evtAgendaChange', fn: onAgendaChanged },
							{ topic: 'evtAgendaChanged', fn: onAgendaChanged },
							{ topic: 'evtAppointmentDeleted', fn: onAppointmentDeleted },
						];

						clearInterval(checkReadyInterval);
						checkReadyInterval = null;
					}
				}
			} catch (e) {
				// DOM not ready yet - continue checking
			}

			// Cap the poll so a page that never exposes externis (or a reload that
			// interrupts before success) can't leave this interval running forever.
			if (attempts >= CHECK_READY_MAX_ATTEMPTS && checkReadyInterval) {
				clearInterval(checkReadyInterval);
				checkReadyInterval = null;
				log('warn', 'iframeWatcher', 'externis/amplify not ready after max attempts — giving up for this load');
			}
		}, 300);
	};
}
